import * as THREE from 'three';
import { XR_MAX_FRAME_DELTA, XR_TRANSLATION_SPEED, XR_TRAVEL_ACCELERATION_SECONDS, XR_TRAVEL_BRAKING_SECONDS, XR_TRAVEL_CORRIDOR_SLOPE, XR_TRAVEL_EMPTY_ACCELERATION_SECONDS, XR_TRAVEL_LOOK_AHEAD_SECONDS } from '../_data/spaceCanvas.js';
export const XR_TRAVEL_SURFACE = { xrTravelSurface: true };
/** Only entity surfaces participate, never labels, guide lines, controllers or HUD geometry. */
export function collectXrTravelGeometry(root) {
    const spheres = [];
    root.updateWorldMatrix(true, true);
    root.traverseVisible(object => {
        if (!(object instanceof THREE.Mesh) || object.userData.xrTravelSurface !== true)
            return;
        for (let ancestor = object; ancestor; ancestor = ancestor.parent) {
            if (ancestor.userData.xrTravelIgnore === true)
                return;
            if (ancestor === root)
                break;
        }
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        if (!materials.some(material => material.visible && material.opacity > 0))
            return;
        const geometry = object.geometry;
        if (!geometry.boundingSphere)
            geometry.computeBoundingSphere();
        if (!geometry.boundingSphere)
            throw new Error('XR travel surface has no bounding sphere.');
        const sphere = geometry.boundingSphere.clone().applyMatrix4(object.matrixWorld);
        if (![...sphere.center.toArray(), sphere.radius].every(Number.isFinite) || sphere.radius < 0) {
            throw new Error('XR travel surface has invalid world bounds.');
        }
        if (sphere.radius > 0)
            spheres.push(sphere);
    });
    const radii = spheres.map(sphere => sphere.radius).sort((a, b) => a - b);
    const scale = radii.length ? radii[Math.floor(radii.length / 2)] : 1;
    const box = new THREE.Box3();
    for (const sphere of spheres) {
        box.expandByPoint(sphere.center.clone().addScalar(sphere.radius));
        box.expandByPoint(sphere.center.clone().addScalar(-sphere.radius));
    }
    return { spheres, scale, extent: box.isEmpty() ? scale : box.getSize(new THREE.Vector3()).length() };
}
export const createXrTravelState = () => ({ speed: 0, direction: undefined, gain: 1 });
export function resolveXrTravel(args) {
    const { eye, geometry, previous, multiplier, throttle } = args;
    if (![geometry.scale, multiplier].every(value => Number.isFinite(value) && value > 0)
        || !Number.isFinite(geometry.extent) || geometry.extent < 0
        || !Number.isFinite(args.delta) || !Number.isFinite(throttle) || throttle < 0 || throttle > 1
        || ![...eye.toArray(), ...args.direction.toArray()].every(Number.isFinite)) {
        throw new Error('Invalid XR adaptive travel input.');
    }
    const dt = Math.min(Math.max(0, args.delta), XR_MAX_FRAME_DELTA);
    if (throttle === 0 || args.direction.lengthSq() === 0) {
        return { state: createXrTravelState(), distance: 0, speed: 0, clearance: Infinity };
    }
    const direction = args.direction.clone().normalize();
    const gain = multiplier * throttle;
    const previousSpeed = previous.direction && previous.direction.dot(direction) > 0.7
        ? previous.speed * gain / (previous.gain ?? 1) : 0;
    const scale = geometry.scale;
    let clearance = Infinity;
    let reach = geometry.extent;
    const offset = new THREE.Vector3();
    for (const sphere of geometry.spheres) {
        offset.copy(sphere.center).sub(eye);
        reach = Math.max(reach, offset.length() + sphere.radius);
        const along = offset.dot(direction);
        // A surface behind the traveller must not brake movement away from it.
        if (along <= 0)
            continue;
        const lateral = Math.sqrt(Math.max(0, offset.lengthSq() - along * along));
        const corridorRadius = scale * 0.5 + along * XR_TRAVEL_CORRIDOR_SLOPE;
        if (lateral > sphere.radius + corridorRadius)
            continue;
        const entry = along - Math.sqrt(Math.max(0, (sphere.radius + corridorRadius) ** 2 - lateral ** 2));
        clearance = Math.min(clearance, Math.max(0, entry));
    }
    const base = XR_TRANSLATION_SPEED * scale * multiplier * throttle;
    const cap = Math.max(base, previousSpeed, reach / XR_TRAVEL_LOOK_AHEAD_SECONDS * gain);
    const target = Math.min(cap, Math.max(base * 0.05, clearance / XR_TRAVEL_LOOK_AHEAD_SECONDS * multiplier * throttle));
    const time = target < previousSpeed ? XR_TRAVEL_BRAKING_SECONDS : XR_TRAVEL_ACCELERATION_SECONDS;
    let speed = Number.isFinite(clearance)
        ? previousSpeed + (target - previousSpeed) * -Math.expm1(-dt / time)
        : previousSpeed * Math.exp(dt / XR_TRAVEL_EMPTY_ACCELERATION_SECONDS)
            + base * XR_TRAVEL_EMPTY_ACCELERATION_SECONDS / XR_TRAVEL_ACCELERATION_SECONDS
                * Math.expm1(dt / XR_TRAVEL_EMPTY_ACCELERATION_SECONDS);
    // Predictive braking bounds each step even after boosting or changing direction.
    if (Number.isFinite(clearance) && dt > 0) {
        speed = Math.min(speed, Math.max(base * 0.05, clearance * 0.2 / dt));
    }
    speed = Math.min(speed, cap);
    return { state: { speed, direction, gain }, distance: speed * dt, speed, clearance };
}
